import classNames from 'classnames';
import { debounce } from 'lodash-es';
import type { ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import SVGIcon from '../components/SVGIcon';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import topicIcon from '../icons/prefilled/16x16/topic.svg';
import TopicBreadcrumbs from '../topic/TopicBreadcrumbs';
import TopicNameWithIcon from '../topic/TopicNameWithIcon';
import Link from '../utils/router/Link';
import topicPath from '../utils/url/topicPath';
import * as styles from './PostSectionHeader.module.scss';

const SUBTOPIC_HEADER_RESIZE_DEBOUNCE = 100;

const TOPIC_FRAGMENT = gql(`
  fragment PostSectionHeader_topic on TopicFields {
    id
    ...topicPath_topic
    ...TopicNameWithIcon_topic
  }
`);

const ANCESTORS_FRAGMENT = gql(`
  fragment PostSectionHeader_ancestors on TopicFields {
    id
    ...TopicBreadcrumbs_topics
  }
`);

type PostSectionHeaderProps = {
  controls?: React.ReactNode;
} & (
  | { title: ReactNode; topic?: never; ancestors?: never }
  | {
      title?: never;
      topic: DocumentType<typeof TOPIC_FRAGMENT>;
      ancestors: DocumentType<typeof ANCESTORS_FRAGMENT>[];
    }
);

const PostSectionHeader = ({
  controls,
  title,
  topic,
  ancestors,
}: PostSectionHeaderProps) => {
  return (
    <div className={styles.header}>
      <div
        className={classNames(styles.content, {
          [styles.canShrink]: !!topic && !!ancestors,
        })}
      >
        {!!title && <h4 className={styles.title}>{title}</h4>}
        {!!topic && !!ancestors && (
          <SubtopicPostsSectionHeader topic={topic} ancestors={ancestors} />
        )}
      </div>
      {!!controls && <div className={styles.controls}>{controls}</div>}
    </div>
  );
};

interface SubtopicSectionHeaderProps {
  ancestors: DocumentType<typeof ANCESTORS_FRAGMENT>[];
  topic: DocumentType<typeof TOPIC_FRAGMENT>;
}

const SubtopicPostsSectionHeader = ({
  ancestors,
  topic,
}: SubtopicSectionHeaderProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!ref.current) {
      return undefined;
    }

    const container = ref.current;
    const onResize = debounce(() => {
      setWidth(container.clientWidth);
    }, SUBTOPIC_HEADER_RESIZE_DEBOUNCE);

    onResize();
    onResize.flush();

    const observer = new ResizeObserver(onResize);
    observer.observe(container);

    return () => {
      observer.disconnect();
      onResize.cancel();
    };
  }, []);

  return (
    <div className={styles.subtopicWrapper} ref={ref} data-width={width}>
      <SVGIcon aria-hidden>{topicIcon}</SVGIcon>
      <div className={styles.subtopicContent}>
        <TopicBreadcrumbs
          link
          topics={ancestors}
          size="regular"
          truncate
          maximumAllowedSpace={width}
        />
        <Link className={styles.subtopicTitleLink} to={topicPath(topic)}>
          <TopicNameWithIcon topic={topic} iconSize="medium" truncate />
        </Link>
      </div>
    </div>
  );
};

export default PostSectionHeader;
